import { format } from "date-fns";
import { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { Link } from "react-router-dom";
import { useAPI } from "../../providers/ApiProvider";
import { cn } from "../../utils/bem";
import "./VersionNotifier.prefix.css";

const VersionContext = createContext();

export const VersionProvider = ({ children }) => {
  const api = useAPI();

  const [state, dispatch] = useReducer((state, action) => {
    if (action.type === "fetch-version") {
      return { ...state, ...action.payload };
    }
  });

  const fetchVersion = useCallback(async () => {
    const response = await api.callApi("version");

    // ``response !== null`` isn't enough: when the cloud is
    // unreachable (Capacitor offline / panel.py stop_cloud) the
    // proxy can resolve to an error envelope that has no
    // ``label-studio-os-package`` key, and reading ``.version``
    // off ``undefined`` throws — that's the boot-time
    // ``main.js:177236`` crash. Guard the inner key + treat any
    // shape mismatch as "no version info available", which leaves
    // the menubar's version badge empty (acceptable degradation
    // when offline).
    const data = response && typeof response === "object" ? response["label-studio-os-package"] : null;
    if (!data || typeof data !== "object") return;

    dispatch({
      type: "fetch-version",
      payload: {
        version: data.version,
        latestVersion: data.latest_version_from_pypi,
        newVersion: data.current_version_is_outdated,
        updateTime: data.latest_version_upload_time ? format(new Date(data.latest_version_upload_time), "MMM d") : "",
      },
    });
  }, []);

  useEffect(() => {
    fetchVersion();
  }, []);

  return <VersionContext.Provider value={state}>{children}</VersionContext.Provider>;
};

export const VersionNotifier = ({ showCurrentVersion }) => {
  const { version } = useContext(VersionContext) ?? {};

  // The upstream Label Studio "new version available" nag linked to
  // `https://labelstud.io/redirect/update` (an LS-version update ping).
  // That's wrong for a fork — it leaks the upstream brand and nags with
  // LS/PyPI release info that doesn't apply to AfriAnnotate. We drop the
  // nag branch entirely and only ever show the harmless current-version
  // badge, which links to the in-app /version page.
  return version && showCurrentVersion ? (
    <Link className={cn("current-version").toClassName()} to="/version" target="_blank">
      v{version}
    </Link>
  ) : null;
};
